import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Grid, IconButton, Stack, Typography } from '@material-ui/core';
import { Carousel } from 'react-responsive-carousel';
import PreviousIcon from '@material-ui/icons/FastRewind';
import NextIcon from '@material-ui/icons/FastForward';
import PlayIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import { useExperience } from '../contexts/experience';
import String from './String';
import { formatTimer } from '../utils/utils';
import { useTheme } from '../contexts/theme';
import { useLanguage } from '../contexts/language';

const btnStyle = {
  position: 'relative',
  border: '2px solid',
  height: '6rem',
  width: '6rem',
};
const countDownStyle = {
  position: 'absolute',
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-around',
  alignItems: 'center',
};

const progressStyle = { position: 'absolute', height: '107%', width: '107%' };

function Player() {
  const { active, stopAll } = useExperience();
  const [selected, setSelected] = useState(0);
  const { content } = useLanguage();

  const isGeo = useMemo(() => {
    const geo = active.filter((s) => s.position);
    return geo && geo?.length > 0;
  }, [active]);

  const handleSelected = React.useCallback(() => {
    if (!active) return;
    const needsToStart = active.findIndex((spot) => !spot.audio.playing() && spot.audio.seek() === 0);
    if (needsToStart > -1) setSelected(needsToStart);
    else setSelected(0);
  }, [active, setSelected]);

  useEffect(() => {
    handleSelected();
  }, [active]);

  return (
    <Stack justifyContent="center" position="relative" width="100%" height="100%">
      <Carousel
        showThumbs={false}
        autoPlay={false}
        infiniteLoop={false}
        showStatus={false}
        showIndicators={false}
        selectedItem={selected}
        onChange={setSelected}
        renderArrowPrev={(clickHandler, hasPrev) => (
          <Button
            color="secondary"
            onClick={clickHandler}
            disabled={!hasPrev}
            aria-hidden={!hasPrev}
            variant="contained"
            aria-label={content('aria_previous_element')}
            style={{
              opacity: hasPrev ? 1 : 0,
              position: 'absolute',
              left: '2vh',
              bottom: '2vh',
              paddingInline: '10px',
              zIndex: 40,
            }}
          >
            <ArrowBackIosIcon style={{ fontSize: '2vh' }} />
          </Button>
        )}
        renderArrowNext={(clickHandler, hasNext) => (
          <Button
            color="secondary"
            onClick={clickHandler}
            disabled={!hasNext}
            aria-hidden={!hasNext}
            variant="contained"
            aria-label={content('aria_next_element')}
            style={{
              opacity: hasNext ? 1 : 0,
              position: 'absolute',
              right: '2vh',
              bottom: '2vh',
              paddingInline: '10px',
              zIndex: 40,
            }}
          >
            <ArrowForwardIosIcon style={{ fontSize: '2vh' }} />
          </Button>
        )}
      >
        {active.map((spot) => (
          <AudioPlayer key={spot.id} data={spot} onUnmount={handleSelected} />
        ))}
      </Carousel>
      {active && active.length > 1 && !isGeo && (
        <Button
          color="secondary"
          variant="contained"
          onClick={stopAll}
          aria-label={content('stop_all')}
          style={{
            position: 'absolute',
            bottom: '2vh',
            left: '50%',
            transform: 'translate(-50%, 0)',
          }}
        >
          <String s="stop_all" />
        </Button>
      )}
    </Stack>
  );
}

function AudioPlayer({ data, onUnmount }) {
  const { styles } = useTheme();
  const { content } = useLanguage();
  const [progress, setProgress] = useState(undefined);

  const description = data?.description;

  const audio = data?.audio;
  // eslint-disable-next-line
  const duration = audio?._duration;
  const perc = (progress / duration) * 100;

  const elapsed = formatTimer(progress) || '00 : 00';
  const totalDuration = formatTimer(duration) || '00:00';

  const srElapsedTemplate =
    content('aria_audio_elapsed_time') ?? 'Elapsed time {elapsed}, of total duration {total}';
  const srElapsedSentence = srElapsedTemplate.replace('{elapsed}', elapsed).replace('{total}', totalDuration);

  const skip = (seconds) => {
    const newProgress = progress + seconds;
    if (newProgress > 0 && newProgress < duration) audio?.seek(newProgress);
  };

  const isGeo = useMemo(() => !!data?.position, [data]);

  const togglePlay = () => {
    if (audio?.playing()) audio.pause();
    else audio.play();
  };

  useEffect(() => {
    const seek = setInterval(() => {
      try {
        const currentProgress = audio?.seek();
        setProgress(currentProgress);
      } catch {
        // do nothing
      }
    }, 100);
    return () => clearInterval(seek);
  }, [audio]);

  useEffect(() => () => onUnmount(), []);

  return (
    <Stack spacing={5} alignItems="center" justifyContent="center" height="100%" pt={4} px={3}>
      <Typography color="textSecondary" variant="h5" fontWeight="bold">
        {data?.label}
      </Typography>

      <Box position="relative">
        {progress === 0 && (
          <Stack
            position="absolute"
            zIndex={1}
            alignItems="center"
            spacing={2}
            width="100%"
            height="100%"
            style={{ background: styles.palette.background.default }}
          >
            <Typography color="textSecondary" variant="text">
              <String s="audio_ready" />
            </Typography>
            <Button color="primary" variant="contained" onClick={togglePlay}>
              <String s="start" />
            </Button>
          </Stack>
        )}

        <Grid
          container
          columnGap={3}
          justifyContent="center"
          alignItems="center"
          data-testid="agami-audio-player"
        >
          <Grid item>
            <IconButton
              color="secondary"
              onClick={() => skip(-10)}
              aria-label={content('aria_rewind_10_secs')}
            >
              <PreviousIcon style={{ fontSize: '2rem' }} />
            </IconButton>
            <Typography fontSize="0.75rem" color="textSecondary" mt={1} aria-hidden role="presentation">
              10 s
            </Typography>
          </Grid>
          <Grid item>
            <IconButton
              onClick={togglePlay}
              style={btnStyle}
              color="secondary"
              data-testid="agami-playback-button"
              aria-label={audio.playing() ? content('pause') : content('play')}
            >
              <Box
                style={countDownStyle}
                data-testid={audio.playing() ? 'agami-pause-icon' : 'agami-play-icon'}
              >
                {audio.playing() ? (
                  <PauseIcon style={{ fontSize: '2rem' }} />
                ) : (
                  <PlayIcon style={{ fontSize: '2rem' }} />
                )}
              </Box>
              <CircularProgress
                variant="determinate"
                value={Math.floor(perc)}
                style={progressStyle}
                thickness={2}
                color="primary"
              />
            </IconButton>
            <Typography className="sr-only">{srElapsedSentence}</Typography>
            <Typography fontSize="0.75rem" color="textSecondary" mt={1} aria-hidden>
              {elapsed}
            </Typography>
          </Grid>
          <Grid item>
            <IconButton
              color="secondary"
              onClick={() => skip(10)}
              aria-label={content('aria_fast_forward_10_secs')}
            >
              <NextIcon style={{ fontSize: '2rem' }} />
            </IconButton>
            <Typography fontSize="0.75rem" color="textSecondary" mt={1} aria-hidden role="presentation">
              10 s
            </Typography>
          </Grid>
        </Grid>
        {!isGeo && (
          <Box mt={2}>
            <Button
              color="secondary"
              variant="outlined"
              onClick={() => audio?.stop()}
              data-testid="agami-stop-btn"
              aria-label={content('stop')}
            >
              <String s="stop" />
            </Button>
          </Box>
        )}
      </Box>

      {description ? (
        <Box flex={1} width="100%" overflow="scroll" borderTop="1px solid gray" py={3}>
          <Typography textAlign="left" fontStyle="italic" style={{ whiteSpace: 'pre-wrap' }}>
            {description}
          </Typography>
        </Box>
      ) : (
        <Typography color="textSecondary" variant="h5" fontWeight="bold" aria-hidden="true">
          <span style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>{data?.label}</span>
        </Typography>
      )}
    </Stack>
  );
}

export default Player;
